/* eslint-disable react/prop-types */
import { useEffect, useState, useCallback } from "react";
import Card from "@mui/material/Card";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import toast from "react-hot-toast";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { CompanyService } from "services/companyService";
import { AccountingStatement3Service } from "services/accounting-statement-3";
import { formatDate, formatVndIntegerValue } from "utils";
import DataTable from "./DataTable";

const currentYear = String(new Date().getFullYear());
const currentMonth = String(new Date().getMonth() + 1);

const initialTableData = {
  columns: [
    { Header: "Số CT", accessor: "voucherNumber", align: "center" },
    { Header: "Ngày CT", accessor: "voucherDate", align: "center" },
    { Header: "Diễn giải", accessor: "description" },
    { Header: "Tổng Nợ TK 113", accessor: "totalDebit113", align: "right" },
  ],
  rows: [],
};

function AccountingTable3() {
  const [companyId, setCompanyId] = useState("");
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [dataTableState, setDataTableState] = useState(initialTableData);
  const [summary, setSummary] = useState({
    totalRows: 0,
    totalDebit113: 0,
  });
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [filters, setFilters] = useState({
    year: currentYear,
    fromDate: "",
    toDate: "",
    accountCode: "",
    searchBy: "",
  });
  const [query, setQuery] = useState({
    year: currentYear,
    fromDate: "",
    toDate: "",
    accountCode: "",
    searchBy: "",
  });
  const [paginationData, setPaginationData] = useState({
    page: 1,
    count: 0,
    size: 10,
  });

  useEffect(() => {
    const init = async () => {
      try {
        const response = await CompanyService.getByAuth();
        const payload = response?.data || response || {};
        setCompanyId(payload?._id || "");
        setCompanyData(payload);
      } catch (error) {
        console.error("Lỗi lấy thông tin công ty:", error);
      }
    };
    init();
  }, []);

  const fetchAccounts = useCallback(async () => {
    if (!companyId || !filters.year) return;
    try {
      setAccountsLoading(true);
      const response = await GeneralJournalService.getCashReceiptJournalAccounts({
        company: companyId,
        year: Number(filters.year),
      });
      const payload = response?.data || response || {};
      const allItems = payload.items || [];
      const items = allItems.filter((acc) => acc.accountCode.startsWith("113"));
      setAccounts(items);

      if (items.length > 0 && !filters.accountCode) {
        const firstAccount = items[0].accountCode;
        setFilters((prev) => ({ ...prev, accountCode: firstAccount }));
        setQuery((prev) => ({ ...prev, accountCode: firstAccount }));
      }
    } catch (error) {
      setAccounts([]);
    } finally {
      setAccountsLoading(false);
    }
  }, [companyId, filters.year, filters.accountCode]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const fetchData = useCallback(async () => {
    if (!companyId || !query.year || !query.accountCode) return;
    try {
      setLoading(true);
      // For Table 3, we still use month logic if BE only supports month, 
      // but let's try to pass dates if we can or just use current month from query
      const month = query.fromDate ? new Date(query.fromDate).getMonth() + 1 : currentMonth;
      
      const response = await AccountingStatement3Service.preview({
        company: companyId,
        year: query.year,
        month: String(month),
        accountCode: query.accountCode,
      });

      const payload = response?.data || response || {};
      const allRows = payload.rows || [];
      const labels = payload.creditAccountLabels || [];
      const totals = payload.totals || {};

      // Update columns based on labels from BE
      const columns = [
        { Header: "Số CT", accessor: "voucherNumber", align: "center" },
        { Header: "Ngày CT", accessor: "voucherDate", align: "center" },
        { Header: "Diễn giải", accessor: "description" },
        ...labels.map((label, index) => ({
          Header: `Ghi Có TK ${label}`,
          accessor: `credit_${index}`,
          align: "right",
        })),
        { Header: "Tổng Nợ TK 113", accessor: "totalDebit113", align: "right" },
      ];

      const normalizedSearch = (query.searchBy || "").trim().toLowerCase();
      const filteredRows = normalizedSearch
        ? allRows.filter((item) =>
            [item.voucherNumber, item.voucherDate, item.description]
              .filter(Boolean)
              .join(" ")
              .toLowerCase()
              .includes(normalizedSearch)
          )
        : allRows;

      const rows = filteredRows.map((item) => {
        const rowData = {
          voucherNumber: item.voucherNumber || "",
          voucherDate: formatDate(item.voucherDate),
          description: item.description || "",
          totalDebit113: formatVndIntegerValue(item.totalDebit113 || 0),
        };

        // Add dynamic credit values
        labels.forEach((_, index) => {
          rowData[`credit_${index}`] = formatVndIntegerValue(item.creditValues?.[index] || 0);
        });

        return rowData;
      });

      setDataTableState({ columns, rows });
      setSummary({
        totalRows: filteredRows.length,
        totalDebit113: totals.totalDebit113 || 0,
        totals: totals, // Store original totals for sync
        creditAccountLabels: labels, // Store labels for sync
      });
      setPaginationData((prev) => ({
        ...prev,
        count: filteredRows.length,
      }));
    } catch (error) {
      console.error("Lỗi lấy dữ liệu Bảng kê số 3:", error);
      toast.error(error?.response?.data?.message || "Không thể tải Bảng kê số 3");
      setDataTableState(initialTableData);
      setSummary({ totalRows: 0, totalDebit113: 0 });
    } finally {
      setLoading(false);
    }
  }, [companyId, query]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSync = async () => {
    if (!companyId || !filters.year) {
      toast.error("Thiếu thông tin công ty hoặc năm");
      return;
    }
    try {
      setSyncing(true);
      const month = filters.fromDate ? new Date(filters.fromDate).getMonth() + 1 : currentMonth;
      
      // Fetch fresh preview data first
      const response = await AccountingStatement3Service.preview({
        company: companyId,
        year: filters.year,
        month: String(month),
        accountCode: filters.accountCode || undefined, // Allow sync even if accountCode not manually selected yet if BE handles it
      });
      const payload = response?.data || response || {};
      
      // Save to database
      await AccountingStatement3Service.create({
        ...payload,
        company: companyId,
        unit: companyData?.name || companyData?.fullName || "N/A",
        address: companyData?.address || "",
        year: String(filters.year),
        month: String(month),
      });

      toast.success("Đồng bộ và lưu Bảng kê số 3 thành công");
      
      // Sync logic similar to Table 1: update query and fetch
      await fetchAccounts();
      setPaginationData((prev) => ({ ...prev, page: 1 }));
      setQuery({ ...filters });
      await fetchData();
    } catch (error) {
      console.error("Lỗi đồng bộ dữ liệu:", error);
      toast.error(error?.response?.data?.message || "Đồng bộ dữ liệu thất bại");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox my={3}>
        <Card style={{ height: "100%" }}>
          <SoftBox p={3} display="flex" justifyContent="space-between" alignItems="center">
            <SoftTypography variant="h6" fontWeight="bold">
              Bảng Kê Số 3 - Tiền đang chuyển (Nợ TK 113)
            </SoftTypography>
          </SoftBox>
          <DataTable
            table={dataTableState}
            loading={loading}
            syncing={syncing}
            filters={filters}
            setFilters={setFilters}
            accounts={accounts}
            accountsLoading={accountsLoading}
            onSync={handleSync}
            onFetchData={(nextFilters) => {
              setQuery((prev) => ({ ...prev, ...nextFilters }));
            }}
            paginationData={paginationData}
            setPaginationData={setPaginationData}
            query={query}
            setQuery={setQuery}
          />
          <SoftBox px={3} pb={3} display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
            <SoftBox display="flex" flexDirection="column">
              <SoftTypography variant="caption" color="text" fontWeight="bold">
                Tổng số dòng: {summary.totalRows}
              </SoftTypography>
            </SoftBox>
            <SoftBox display="flex" flexDirection="column" textAlign="right">
              <SoftTypography variant="caption" color="text" fontWeight="bold">
                Tổng cộng Nợ TK 113: {formatVndIntegerValue(summary.totalDebit113 || 0)}
              </SoftTypography>
            </SoftBox>
          </SoftBox>
        </Card>
      </SoftBox>
    </DashboardLayout>
  );
}

export default AccountingTable3;
